// @flow
import queryString from 'query-string';
import { getJson, postJson } from 'utils/http';
import type { UserMini } from 'api/shared';
import { APPLICATION_STATUSES_ALL } from 'utils/constants/ats';

export type ListingApplicationType = 'JOB' | 'INTERNSHIP' | 'VOLOP';

export type ListingApplicationStatus = $Keys<typeof APPLICATION_STATUSES_ALL>;

type ListingMini = {
  id: string,
  name: string,
  type: ListingApplicationType,
  firstPublished?: string
};

type ListingApplicationFeedEntryBase = {
  id: string,
  created: string,
  byUser: ?UserMini
};

export type LisitngApplicationFeedNote = {
  ...$Exact<ListingApplicationFeedEntryBase>,
  type: 'ADDED_NOTE',
  data: {
    note: string
  }
};

export type ListingApplicationFeedStatus = {
  ...$Exact<ListingApplicationFeedEntryBase>,
  type: 'STATUS_CHANGED',
  data: {
    prevStatus: ?ListingApplicationStatus,
    newStatus: ListingApplicationStatus
  }
};

export type ListingApplicationFeedEntry =
  | LisitngApplicationFeedNote
  | ListingApplicationFeedStatus;

export type ListingApplicationMini = {
  id: string,
  created: string,
  status: ListingApplicationStatus,
  user: UserMini,
  listing: ListingMini,
  lastFeedEntry: ?ListingApplicationFeedEntry
};

export type File = {
  filename: string,
  id: string,
  mimetype: string,
  size: number
};

export type ListingApplication = {
  id: string,
  created: string,
  user: UserMini,
  status: ListingApplicationStatus,
  listing: ListingMini,
  feedEntries: Array<ListingApplicationFeedEntry>,
  files: {
    resume: File,
    requestedAttachments: Array<{ file: File, label: string }>
  },
  referrer: ?string,
  attachments: {
    resume: File,
    requestedAttachments: Array<
      | { type: 'FILE', file: File, label: string, url: null }
      | { type: 'URL', file: null, label: string, url: string }
    >
  }
};

export const fetchApplications = (
  orgId: string,
  params: {
    type: ?ListingApplicationType,
    id: ?string,
    page: ?number
  }
) => {
  const qs = queryString.stringify(params);
  return getJson(`/data/orgdashboard/${orgId}/listing-applications?${qs}`);
};

export const fetchApplicationIds = (
  orgId: string,
  params: {
    id: ?string
  }
) => {
  const qs = queryString.stringify(params);
  return getJson(
    `/data/orgdashboard/${orgId}/listing-applications-ids?${qs}`
  ).then(({ listingApplicationIds }) => listingApplicationIds);
};

export const fetchApplication: ({
  orgId: string,
  applicationId: string
}) => Promise<ListingApplication> = ({ orgId, applicationId }) =>
  getJson(
    `/data/orgdashboard/${orgId}/listing-applications/${applicationId}`
  ).then(({ listingApplication }) => listingApplication);

export type RequestedAttachmentType =
  | { type: 'FILE', s3FileId: string }
  | { type: 'URL', url: string };

export type ListingApplicationPayload = {
  id: string,
  firstName: ?string,
  lastName: ?string,
  email: ?string,
  requestedAttachments: Array<RequestedAttachmentType>,
  resumeS3FileId: string,
  replaceCurrentResume: boolean,
  type: ListingApplicationType,
  consentAts?: boolean
};

export const createApplication = (payload: ListingApplicationPayload) =>
  postJson('/data/listing-applications', payload).then(response => response);

export const createApplicationNote: ({
  note: string,
  orgId: string,
  applicationId: string
}) => Promise<ListingApplication> = ({ orgId, applicationId, ...data }) =>
  postJson(
    `/data/orgdashboard/${orgId}/listing-applications/${applicationId}/note`,
    data
  ).then(({ listingApplication }) => listingApplication);

export const updateApplicationStatus: ({
  status: ListingApplicationStatus,
  orgId: string,
  applicationId: string
}) => Promise<ListingApplication> = ({ orgId, applicationId, ...data }) =>
  postJson(
    `/data/orgdashboard/${orgId}/listing-applications/${applicationId}/status`,
    data
  ).then(({ listingApplication }) => listingApplication);

export function validateApplication(
  payload: Object,
  params?: { partial: boolean }
): Promise<{ listingApplication: ListingApplicationPayload }> {
  const qs = queryString.stringify(params);
  return postJson(
    `/data/listing-applications-validation${qs ? `?${qs}` : ''}`,
    payload
  );
}
